import { Box, Button, ButtonGroup, Icon, LinkButton, Text, Link } from '@homebotapp/hb-react-component-catalog'
import classNames from 'classnames'
import { defineMessages, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { Link as ReactRouterLink } from 'react-router-dom'
import { Logo } from '../../../../Components'
import { toggleAuthModal } from '../../../../actions/homeSearchAuthModal'
import publicLogo from '../../../../assets/publicHomSearch/HB-real-estate.png'
import { GlobalTrackingEventLocation } from '../../../../constants/tracking'
import { useTrackingContext } from '../../../../providers/tracking'
import { selectClientIsGuest } from '../../../../store/selectors/client'
import { selectRouteToFavoriteListings, selectRouteToSavedSearches } from '../../../../store/selectors/routes'
import { setClickedListItem } from '../../../../store/slices/nav'
import { NavigationComponents } from '../../../../types/navigation'
import { RootState } from '../../../../types/rootState'
import AgentDropdown from '../AgentDropdown'
import { MobileHeaderContainer } from '../MobileHeaderContainer/MobileHeaderContainer'
import styles from './MobileSearchHeader.module.scss'

export const MSG = defineMessages({
  alt: {
    id: 'MobileHeader.publicSearch.logo.alt',
    defaultMessage: 'Homebot Real Estate Services'
  },
  signup: {
    id: 'MobileSearchHeader.signup',
    defaultMessage: 'Sign up'
  },
  login: {
    id: 'MobileSearchHeader.login',
    defaultMessage: 'Log in'
  },
  favorites: {
    id: 'MobileSearchHeader.favorites',
    defaultMessage: 'Favorites'
  },
  searches: {
    id: 'MobileSearchHeader.searches',
    defaultMessage: 'Searches'
  }
})

export interface MobileHeaderProps {
  enableAgentDropdown?: boolean
  className?: string
}

export const MobileSearchHeader = ({ enableAgentDropdown, className }: MobileHeaderProps) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const { trackingClient } = useTrackingContext()
  const isPublic = useSelector<RootState, boolean>(state => state.auth.isPublic)
  const isGuest = useSelector(selectClientIsGuest)
  const favoriteListingsTo = useSelector(selectRouteToFavoriteListings)
  const savedSearchesTo = useSelector(selectRouteToSavedSearches)

  const mappedKeyToRoute = {
    [NavigationComponents.FAVORITE_LISTINGS]: favoriteListingsTo,
    [NavigationComponents.SAVED_SEARCHES]: savedSearchesTo
  }

  const handleOnLinkClick = (key: NavigationComponents) => {
    // todo - check if we need this since we have tracking in the buttons now
    // trackingClient?.buyerNavigationLinkClicked({
    //   buyer_navigation_link: buyerLinksMappedToAmplitude[key]
    // })
    dispatch(setClickedListItem(key))
  }

  const handleSignupSigninClick = () => {
    dispatch(toggleAuthModal(true))
  }

  return (
    <MobileHeaderContainer>
      {isPublic ? (
        <Link as={ReactRouterLink} to='/home-search/public'>
          <img width={72} height={72} src={publicLogo} alt={intl.formatMessage(MSG.alt)} />
        </Link>
      ) : (
        <>
          <div className={classNames(styles.col, styles.logo)}>
            <Logo className={styles.logo} />
            {isGuest && (
              <Text size='lg' marginBottom={0}>
                Homebot
              </Text>
            )}
          </div>

          <div className={styles.col}>
            {isGuest ? (
              <ButtonGroup alignItems='center'>
                <Button
                  onClick={handleSignupSigninClick}
                  size='sm'
                  variant='ghost'
                  tracking={{
                    guid: 'uc46wX131Xdv16Lg34dxXN',
                    ui_context: GlobalTrackingEventLocation.GLOBAL_MOBILE_TOP_NAV_FOR_SEARCH,
                    descriptor: MSG.signup.defaultMessage
                  }}
                >
                  {intl.formatMessage(MSG.signup)}
                </Button>
                <Box w='2px' h='20px' bgColor='neutral.300'></Box>
                <Button
                  onClick={handleSignupSigninClick}
                  size='sm'
                  variant='ghost'
                  tracking={{
                    guid: 'aAaX4VZTu5qyvdGyQJBvaF',
                    ui_context: GlobalTrackingEventLocation.GLOBAL_MOBILE_TOP_NAV_FOR_SEARCH,
                    descriptor: MSG.login.defaultMessage
                  }}
                >
                  {intl.formatMessage(MSG.login)}
                </Button>
              </ButtonGroup>
            ) : (
              <ButtonGroup>
                <LinkButton
                  as={ReactRouterLink}
                  to={mappedKeyToRoute[NavigationComponents.FAVORITE_LISTINGS]}
                  onClick={() => handleOnLinkClick(NavigationComponents.FAVORITE_LISTINGS)}
                  size='sm'
                  variant='outline'
                  colorScheme='primary'
                  leftIcon={<Icon name='heart' />}
                  tracking={{
                    guid: 'kRzoMddxc3pcJPZBdVsAwa',
                    ui_context: GlobalTrackingEventLocation.GLOBAL_MOBILE_TOP_NAV_FOR_SEARCH,
                    descriptor: MSG.favorites.defaultMessage
                  }}
                >
                  {intl.formatMessage(MSG.favorites)}
                </LinkButton>
                <LinkButton
                  as={ReactRouterLink}
                  to={mappedKeyToRoute[NavigationComponents.SAVED_SEARCHES]}
                  onClick={() => handleOnLinkClick(NavigationComponents.SAVED_SEARCHES)}
                  size='sm'
                  variant='outline'
                  colorScheme='primary'
                  leftIcon={<Icon name='bell-filled' />}
                  tracking={{
                    guid: 'mfak8RSUSZMJJFmLDQ9vPT',
                    ui_context: GlobalTrackingEventLocation.GLOBAL_MOBILE_TOP_NAV_FOR_SEARCH,
                    descriptor: MSG.searches.defaultMessage
                  }}
                >
                  {intl.formatMessage(MSG.searches)}
                </LinkButton>
              </ButtonGroup>
            )}
            {enableAgentDropdown && <AgentDropdown />}
          </div>
        </>
      )}
    </MobileHeaderContainer>
  )
}
